// Purchase date for a position: an exact day, an approximate month («не помню
// точно»), or none. It is a calendar date, not a moment — stored as
// 'YYYY-MM-DD' / 'YYYY-MM' and never shifted by the time zone. «Today» (the
// latest date allowed) is today in the user's zone.
import { useT } from '@/i18n';
import { useState } from 'react';
import { Segmented } from '@/components/ui/Controls';
import { Icon } from '@/components/ui/Icon';
import { useTime } from '@/hooks/useTime';
import { addDays, dateOnly, monthLabel, monthNames, weekdayNames } from '@/lib/time';
import { cx } from '@/lib/format';

export type PDate = { mode: 'exact' | 'approx' | 'none'; since?: string };
/** a purchase's date as the picker shows it: a day the user named, or the month they named (a day found by price history keeps its month) */
export const pdateOf = (l?: { date?: string; dateSource?: 'user' | 'history'; period?: string }): PDate =>
  !l ? { mode: 'exact' } : l.period ? { mode: 'approx', since: l.period } : l.date ? { mode: 'exact', since: l.date } : { mode: 'none' };

const pad = (n: number) => String(n).padStart(2, '0');
const ym = (y: number, m: number) => `${y}-${pad(m)}`;

export function PurchaseDate({ value, onChange, estimating }: { value: PDate; onChange: (d: PDate) => void; estimating?: boolean }) {
  const tf = useTime();
  const { t } = useT('portfolio');
  const today = tf.todayKey();
  const MONTHS = monthNames('long');
  const MON_SHORT = monthNames('short');
  const WD = weekdayNames();
  const [ty, tm] = today.split('-').map(Number);
  const sel = value.since;
  const [view, setView] = useState(() => { const k = sel ?? today; return { y: Number(k.slice(0, 4)), m: Number(k.slice(5, 7)) }; });
  const [open, setOpen] = useState(value.mode === 'exact' && !sel);

  const setMode = (mode: PDate['mode']) => {
    if (mode === 'none') onChange({ mode });
    // switching keeps what can be kept: a day → its month, a month → no day yet
    else if (mode === 'approx') onChange({ mode, since: sel?.slice(0, 7) });
    else { onChange({ mode, since: sel && sel.length === 10 ? sel : undefined }); setOpen(true); }
  };
  const pickDay = (k: string) => { onChange({ mode: 'exact', since: k }); setOpen(false); };
  const step = (dir: -1 | 1) => setView(({ y, m }) => { const n = m + dir; return n < 1 ? { y: y - 1, m: 12 } : n > 12 ? { y: y + 1, m: 1 } : { y, m: n }; });
  const canNext = view.y < ty || (view.y === ty && view.m < tm);

  // the month grid: Monday first
  const first = new Date(Date.UTC(view.y, view.m - 1, 1)).getUTCDay();
  const lead = (first + 6) % 7;
  const daysIn = new Date(Date.UTC(view.y, view.m, 0)).getUTCDate();
  const quick: Array<[string, string]> = [[t('common:time.today'), today], [t('common:time.yesterday'), addDays(today, -1)], [t('date.weekAgo'), addDays(today, -7)], [t('date.monthAgo'), addDays(today, -30)]];

  return (
    <div className="pdate">
      <div className="pdate-h">
        <span className="pdate-l">{t('date.label')}</span>
        <Segmented className="sr-seg pdate-seg" label={t('date.label')} value={value.mode} onChange={setMode}
          options={[{ value: 'exact', label: t('date.exact') }, { value: 'approx', label: t('date.approx') }, { value: 'none', label: t('date.none') }]} />
      </div>

      {value.mode === 'exact' && (
        <>
          <button type="button" className={cx('pdate-field', !sel && 'empty')} aria-expanded={open} onClick={() => setOpen(!open)}>
            <Icon name="cal" /><span>{sel ? dateOnly(sel) : t('date.pickDay')}</span><Icon name="chev" className={cx('pdate-caret', open && 'up')} />
          </button>
          <div className="pdate-quick">
            {quick.map(([l, k]) => <button key={l} type="button" className={cx(sel === k && 'on')} onClick={() => { pickDay(k); setView({ y: Number(k.slice(0, 4)), m: Number(k.slice(5, 7)) }); }}>{l}</button>)}
          </div>
          {open && (
            <div className="pdate-cal" role="group" aria-label={t('date.calendar')}>
              <div className="pdate-nav">
                <button type="button" aria-label={t('date.prevMonth')} onClick={() => step(-1)}><Icon name="chevl" /></button>
                <b>{MONTHS[view.m - 1]} {view.y}</b>
                <button type="button" aria-label={t('date.nextMonth')} disabled={!canNext} onClick={() => step(1)}><Icon name="chev" /></button>
              </div>
              <div className="pdate-grid">
                {WD.map((w, i) => <span key={i} className="pdate-wd">{w}</span>)}
                {Array.from({ length: lead }, (_, i) => <span key={`e${i}`} />)}
                {Array.from({ length: daysIn }, (_, i) => {
                  const k = `${ym(view.y, view.m)}-${pad(i + 1)}`;
                  return <button key={k} type="button" disabled={k > today} className={cx(k === sel && 'on', k === today && 'today')} aria-pressed={k === sel} aria-label={dateOnly(k)} onClick={() => pickDay(k)}>{i + 1}</button>;
                })}
              </div>
            </div>
          )}
        </>
      )}

      {value.mode === 'approx' && (
        <div className="pdate-cal">
          <div className="pdate-nav">
            <button type="button" aria-label={t('date.prevYear')} onClick={() => setView((v) => ({ ...v, y: v.y - 1 }))}><Icon name="chevl" /></button>
            <b>{view.y}</b>
            <button type="button" aria-label={t('date.nextYear')} disabled={view.y >= ty} onClick={() => setView((v) => ({ ...v, y: v.y + 1 }))}><Icon name="chev" /></button>
          </div>
          <div className="pdate-months">
            {MON_SHORT.map((l, i) => {
              const k = ym(view.y, i + 1);
              return <button key={k} type="button" disabled={k > today.slice(0, 7)} className={cx(k === sel && 'on')} aria-pressed={k === sel} onClick={() => onChange({ mode: 'approx', since: k })}>{l}</button>;
            })}
          </div>
          <p className="pdate-note">{sel ? estimating ? t('date.approxEstimate', { month: monthLabel(sel) }) : t('date.approxCheck', { month: monthLabel(sel) }) : t('date.pickMonth')}</p>
        </div>
      )}

      {value.mode === 'none' && <p className="pdate-note">{t('date.noneNote')}</p>}
    </div>
  );
}
